/**
 * Pilot Management Section
 * Settings page section for managing pilot accounts.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 8.7, 8.8, 8.14, 8.15, 8.16, 9.1–9.7, 10.1–10.5, 11.1–11.2, 11.4–11.7
 */

import { createDataGrid } from '../data-grid.js';
import { openDetailModal } from '../detail-modal.js';
import {
  getPilots,
  createPilotWithAuth,
  updatePilot,
  deactivatePilot,
  reactivatePilot,
  deletePilot,
  resetUserPassword,
  setUserPassword,
  getAdminCount,
  getPilotLicences,
  getPilotFlyingHours,
} from '../../services/api.js';
import { validateEmail, validateTemporaryPassword } from '../../utils/validation.js';
import { getUser } from '../../services/auth.js';
import { USER_ROLES } from '../../utils/constants.js';
import { openMergeDialog } from '../merge-dialog.js';
import { resolveDisplayName } from '../../utils/alias-resolution.js';

/**
 * Escape a string for safe insertion into HTML.
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Filter pilots by alias visibility.
 * When showAliases is false (default), alias records are excluded.
 *
 * Exported for property-based testing (Property 12).
 *
 * @param {Object[]} pilots - Array of pilot records
 * @param {boolean} showAliases - Whether to include alias records
 * @returns {Object[]}
 */
export function filterPilotsByAlias(pilots, showAliases) {
  if (showAliases) return pilots;
  return pilots.filter((p) => p.role !== 'alias');
}

/**
 * Returns the enabled state of Merge and Delete buttons based on the
 * number of currently selected pilots.
 *
 * Exported for property-based testing (Property 7).
 *
 * @param {number} selectedCount - Number of selected pilots
 * @returns {{ mergeEnabled: boolean, deleteEnabled: boolean }}
 */
export function getMultiSelectButtonStates(selectedCount) {
  return {
    mergeEnabled: selectedCount >= 2,
    deleteEnabled: selectedCount >= 1,
  };
}

/**
 * createPilotManagement() → HTMLElement
 *
 * Returns a section element containing the pilot management Data_Grid
 * with add, edit, deactivate/reactivate capabilities and multi-select mode.
 */
export function createPilotManagement() {
  const section = document.createElement('section');
  section.className = 'settings-section pilot-management';

  /** @type {Object[]} */
  let pilots = [];

  // ── Alias filter state ─────────────────────────────────────────────────
  let showAliases = false;

  // ── Multi-select state ─────────────────────────────────────────────────
  let multiSelectActive = false;
  /** @type {Set<string>} */
  const selectedPilotIds = new Set();

  // ── Toolbar controls ────────────────────────────────────────────────────
  const selectBtn = document.createElement('button');
  selectBtn.className = 'btn pilot-management__select-btn';
  selectBtn.textContent = 'Select';
  selectBtn.addEventListener('click', () => {
    multiSelectActive = !multiSelectActive;
    if (!multiSelectActive) {
      selectedPilotIds.clear();
    }
    selectBtn.textContent = multiSelectActive ? 'Cancel' : 'Select';
    renderGrid();
  });

  // "Show aliases" toggle
  const aliasToggle = document.createElement('label');
  aliasToggle.className = 'pilot-management__alias-toggle';
  aliasToggle.style.cssText = 'display:inline-flex;align-items:center;gap:0.375rem;font-size:0.85rem;cursor:pointer;user-select:none;';
  const aliasCheckbox = document.createElement('input');
  aliasCheckbox.type = 'checkbox';
  aliasCheckbox.checked = false;
  aliasCheckbox.addEventListener('change', () => {
    showAliases = aliasCheckbox.checked;
    renderGrid();
  });
  aliasToggle.appendChild(aliasCheckbox);
  aliasToggle.appendChild(document.createTextNode('Show aliases'));

  const mergeBtn = document.createElement('button');
  mergeBtn.className = 'btn btn--primary pilot-management__merge-btn';
  mergeBtn.textContent = 'Merge';
  mergeBtn.disabled = true;
  mergeBtn.addEventListener('click', () => {
    const selectedPilots = pilots.filter((p) => selectedPilotIds.has(p.id));
    openMergeDialog(selectedPilots, async () => {
      selectedPilotIds.clear();
      multiSelectActive = false;
      selectBtn.textContent = 'Select';
      await refreshPilots();
      showMessage('Users merged successfully');
    });
  });

  const bulkDeleteBtn = document.createElement('button');
  bulkDeleteBtn.className = 'btn btn--danger pilot-management__delete-btn';
  bulkDeleteBtn.textContent = 'Delete';
  bulkDeleteBtn.disabled = true;
  bulkDeleteBtn.addEventListener('click', () => handleBulkDelete());

  const gridContainer = document.createElement('div');
  section.appendChild(gridContainer);

  // Message area for success/error feedback
  const messageArea = document.createElement('div');
  messageArea.className = 'pilot-management__message';
  section.appendChild(messageArea);

  /**
   * Show a temporary message in the message area.
   * @param {string} text
   * @param {'success'|'error'} type
   */
  function showMessage(text, type = 'success') {
    messageArea.textContent = text;
    messageArea.className = `pilot-management__message pilot-management__message--${type}`;
    setTimeout(() => {
      messageArea.textContent = '';
      messageArea.className = 'pilot-management__message';
    }, 4000);
  }

  /**
   * Update the toolbar button states based on current selection.
   */
  function updateToolbar() {
    mergeBtn.style.display = multiSelectActive ? '' : 'none';
    bulkDeleteBtn.style.display = multiSelectActive ? '' : 'none';
    const states = getMultiSelectButtonStates(selectedPilotIds.size);
    mergeBtn.disabled = !states.mergeEnabled;
    bulkDeleteBtn.disabled = !states.deleteEnabled;
  }

  /**
   * Build and render the Data_Grid with current pilot data.
   */
  function renderGrid() {
    gridContainer.innerHTML = '';

    // Build columns — prepend checkbox column when multi-select is active
    const columns = [];
    if (multiSelectActive) {
      columns.push({
        key: '_select',
        label: '',
        sortable: false,
        html: true,
        format: (_v, row) => {
          const checked = selectedPilotIds.has(row.id) ? 'checked' : '';
          return `<input type="checkbox" class="pilot-select-checkbox" data-pilot-id="${row.id}" ${checked} />`;
        },
      });
    }
    columns.push(
      {
        key: 'name',
        label: 'Name',
        html: true,
        format: (v, row) => {
          const displayName = resolveDisplayName(row, pilotsMap);
          const nameLink = `<a href="#/users/${row.id}" class="user-detail-link">${escapeHtml(displayName)}</a>`;
          if (row.role === 'alias' && row.parent?.name) {
            return `${nameLink} <span class="badge badge--alias">Alias → ${escapeHtml(row.parent.name)}</span>`;
          }
          return nameLink;
        },
      },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role', format: (v) => (v || '').charAt(0).toUpperCase() + (v || '').slice(1) },
      {
        key: 'active',
        label: 'Status',
        format: (v) => (v === false ? 'Inactive' : 'Active'),
      },
      {
        key: 'guest',
        label: 'Guest',
        html: true,
        format: (v) => (v ? '🏷 Guest' : ''),
      },
      {
        key: 'created_at',
        label: 'Created',
        format: (v) => (v ? new Date(v).toLocaleDateString() : ''),
      },
    );

    const filteredPilots = filterPilotsByAlias(pilots, showAliases);

    // Build a lookup map for alias display resolution (Requirement 8.12)
    const pilotsMap = new Map();
    for (const p of pilots) {
      pilotsMap.set(p.id, p);
    }

    const grid = createDataGrid({
      columns,
      data: filteredPilots,
      defaultSort: 'name',
      defaultOrder: 'asc',
      emptyMessage: 'No pilots found',
      filterable: true,
      heading: 'User Manager',
      addLabel: 'Add Pilot',
      onAdd: () => openAddPilotModal(),
      onRowClick: (row) => openEditPilotModal(row),
      rowClass: (row) => {
        if (row.role === 'alias') return 'pilot-row--alias';
        if (row.active === false) return 'pilot-row--inactive';
        return null;
      },

    });

    gridContainer.appendChild(grid);

    // Create toolbar row and insert it after the header row
    const toolbarRow = document.createElement('div');
    toolbarRow.className = 'settings-section__toolbar-row';
    toolbarRow.appendChild(selectBtn);
    toolbarRow.appendChild(aliasToggle);
    toolbarRow.appendChild(mergeBtn);
    toolbarRow.appendChild(bulkDeleteBtn);

    const headerRowEl = grid.querySelector('.data-grid__header-row');
    if (headerRowEl && headerRowEl.nextSibling) {
      grid.insertBefore(toolbarRow, headerRowEl.nextSibling);
    } else {
      grid.appendChild(toolbarRow);
    }

    // Listen for checkbox clicks inside the grid
    grid.addEventListener('click', (e) => {
      const checkbox = e.target.closest('input[type="checkbox"].pilot-select-checkbox');
      if (!checkbox) return;
      const pilotId = checkbox.dataset.pilotId;
      if (checkbox.checked) {
        selectedPilotIds.add(pilotId);
      } else {
        selectedPilotIds.delete(pilotId);
      }
      updateToolbar();
    });

    updateToolbar();
  }

  /**
   * Open the "Add Pilot" modal.
   */
  function openAddPilotModal() {
    openDetailModal({
      title: 'Add Pilot',
      fields: [
        { name: 'name', label: 'Name', type: 'text', required: true },
        { name: 'email', label: 'Email', type: 'text', required: true },
        {
          name: 'role',
          label: 'Role',
          type: 'select',
          required: true,
          options: [
            { value: USER_ROLES.PILOT, label: 'Pilot' },
            { value: USER_ROLES.ADMIN, label: 'Admin' },
            { value: USER_ROLES.OWNER, label: 'Owner' },
          ],
        },
        { name: 'password', label: 'Temporary Password', type: 'text', required: true },
      ],
      onSave: async (formData) => {
        // Validate email
        const emailResult = validateEmail(formData.email);
        if (!emailResult.valid) {
          const err = new Error(emailResult.errors[0]);
          err.fieldErrors = { email: emailResult.errors[0] };
          throw err;
        }

        // Validate password
        const pwResult = validateTemporaryPassword(formData.password);
        if (!pwResult.valid) {
          const err = new Error(pwResult.errors[0]);
          err.fieldErrors = { password: pwResult.errors[0] };
          throw err;
        }

        try {
          await createPilotWithAuth({
            name: formData.name,
            email: formData.email,
            role: formData.role,
            password: formData.password,
          });
          await refreshPilots();
          showMessage('Pilot created successfully');
        } catch (apiErr) {
          // Handle duplicate email
          const msg = apiErr.message || '';
          if (msg.toLowerCase().includes('already') || msg.includes('23505')) {
            const err = new Error('A user with this email already exists');
            err.fieldErrors = { email: 'A user with this email already exists' };
            throw err;
          }
          throw apiErr;
        }
      },
    });
  }

  /**
   * Open the edit/detail modal for an existing pilot.
   * Shows pilot info, licences, flying hours, and deactivate/reactivate controls.
   * @param {Object} pilot
   */
  async function openEditPilotModal(pilot) {
    // Fetch supplementary data
    let licences = [];
    let flyingHours = { total_minutes: 0, last_90_days_minutes: 0 };
    try {
      [licences, flyingHours] = await Promise.all([
        getPilotLicences(pilot.id),
        getPilotFlyingHours(pilot.id),
      ]);
    } catch (_) {
      // Non-critical — proceed with empty data
    }

    // Build custom sections for licences and flying hours
    const infoSection = document.createElement('div');
    infoSection.className = 'pilot-detail__info';

    // Flying hours summary
    const hoursDiv = document.createElement('div');
    hoursDiv.className = 'pilot-detail__section';
    const totalHrs = (flyingHours.total_minutes / 60).toFixed(1);
    const last90Hrs = (flyingHours.last_90_days_minutes / 60).toFixed(1);
    hoursDiv.innerHTML =
      `<h3 class="pilot-detail__section-heading">Flying Hours</h3>` +
      `<div class="pilot-detail__section-body">` +
      `<span class="pilot-detail__stat">Total: ${totalHrs} hrs</span>` +
      `<span class="pilot-detail__stat">Last 90 days: ${last90Hrs} hrs</span>` +
      `</div>`;
    infoSection.appendChild(hoursDiv);

    // Licences list
    const licDiv = document.createElement('div');
    licDiv.className = 'pilot-detail__section';
    if (licences.length > 0) {
      licDiv.innerHTML =
        '<h3 class="pilot-detail__section-heading">Licences</h3>' +
        '<ul class="pilot-detail__licence-list">' +
        licences.map((l) => `<li>${l.licence_type || l.type || 'Licence'} — expires ${l.expiry_date || 'N/A'}</li>`).join('') +
        '</ul>';
    } else {
      licDiv.innerHTML =
        '<h3 class="pilot-detail__section-heading">Licences</h3>' +
        '<p class="pilot-detail__empty">No licences recorded</p>';
    }
    infoSection.appendChild(licDiv);

    // Deactivate / Reactivate + Password management — combined action row
    const currentUser = getUser();
    const isOwnAccount = currentUser && currentUser.id === pilot.id;

    const actionsSection = document.createElement('div');
    actionsSection.className = 'pilot-detail__section';
    const actionsHeading = document.createElement('h3');
    actionsHeading.className = 'pilot-detail__section-heading';
    actionsHeading.textContent = 'Account';
    actionsSection.appendChild(actionsHeading);

    const actionRow = document.createElement('div');
    actionRow.className = 'pilot-detail__action-row';

    if (pilot.active !== false) {
      // Show Deactivate button (disabled for own account)
      const deactivateBtn = document.createElement('button');
      deactivateBtn.type = 'button';
      deactivateBtn.className = 'btn btn--danger btn--sm';
      deactivateBtn.textContent = 'Deactivate';
      if (isOwnAccount) {
        deactivateBtn.disabled = true;
        deactivateBtn.title = 'You cannot deactivate your own account';
      }
      deactivateBtn.addEventListener('click', async () => {
        try {
          await deactivatePilot(pilot.id);
          await refreshPilots();
          showMessage(`${pilot.name} has been deactivated`);
          // Close modal by dispatching escape
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
        } catch (err) {
          showMessage(err.message || 'Failed to deactivate pilot', 'error');
        }
      });
      actionRow.appendChild(deactivateBtn);
    } else {
      // Show Reactivate button
      const reactivateBtn = document.createElement('button');
      reactivateBtn.type = 'button';
      reactivateBtn.className = 'btn btn--primary btn--sm';
      reactivateBtn.textContent = 'Reactivate';
      reactivateBtn.addEventListener('click', async () => {
        try {
          await reactivatePilot(pilot.id);
          await refreshPilots();
          showMessage(`${pilot.name} has been reactivated`);
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
        } catch (err) {
          showMessage(err.message || 'Failed to reactivate pilot', 'error');
        }
      });
      actionRow.appendChild(reactivateBtn);
    }

    // ── Set Password button + inline form ────────────────────────────────
    const setPasswordBtn = document.createElement('button');
    setPasswordBtn.type = 'button';
    setPasswordBtn.className = 'btn btn--sm';
    setPasswordBtn.textContent = 'Set Password';

    // ── Send Reset Email button ──────────────────────────────────────────
    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.className = 'btn btn--sm';
    resetBtn.textContent = 'Send Reset Email';

    actionRow.appendChild(setPasswordBtn);
    actionRow.appendChild(resetBtn);
    actionsSection.appendChild(actionRow);

    // Error/success message area for password operations
    const pwMessage = document.createElement('div');
    pwMessage.className = 'pilot-detail__password-message';
    pwMessage.style.display = 'none';

    function showPasswordMessage(text, type = 'error') {
      pwMessage.textContent = text;
      pwMessage.style.display = '';
      pwMessage.style.color = type === 'error' ? 'var(--color-danger, #d32f2f)' : 'var(--color-success, #2e7d32)';
      if (type === 'success') {
        setTimeout(() => { pwMessage.style.display = 'none'; }, 4000);
      }
    }

    actionsSection.appendChild(pwMessage);

    // Inline set-password form (hidden by default)
    const setPasswordForm = document.createElement('div');
    setPasswordForm.className = 'pilot-detail__set-password-form';
    setPasswordForm.style.display = 'none';

    const pwInput = document.createElement('input');
    pwInput.type = 'password';
    pwInput.placeholder = 'New password (min 8 chars)';
    pwInput.className = 'input input--sm';
    pwInput.style.cssText = 'width:100%;margin-bottom:0.375rem;';

    const pwFormActions = document.createElement('div');
    pwFormActions.className = 'pilot-detail__action-row';

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'btn btn--sm btn--primary';
    confirmBtn.textContent = 'Confirm';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn--sm';
    cancelBtn.textContent = 'Cancel';

    pwFormActions.appendChild(confirmBtn);
    pwFormActions.appendChild(cancelBtn);
    setPasswordForm.appendChild(pwInput);
    setPasswordForm.appendChild(pwFormActions);

    setPasswordBtn.addEventListener('click', () => {
      setPasswordForm.style.display = '';
      setPasswordBtn.style.display = 'none';
      pwMessage.style.display = 'none';
      pwInput.value = '';
      pwInput.focus();
    });

    cancelBtn.addEventListener('click', () => {
      setPasswordForm.style.display = 'none';
      setPasswordBtn.style.display = '';
      pwMessage.style.display = 'none';
      pwInput.value = '';
    });

    confirmBtn.addEventListener('click', async () => {
      const newPassword = pwInput.value;
      const pwResult = validateTemporaryPassword(newPassword);
      if (!pwResult.valid) {
        showPasswordMessage(pwResult.errors[0]);
        return;
      }
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Setting…';
      try {
        await setUserPassword(pilot.id, newPassword);
        setPasswordForm.style.display = 'none';
        setPasswordBtn.style.display = '';
        pwInput.value = '';
        showPasswordMessage(`Password set for ${pilot.name}`, 'success');
      } catch (err) {
        showPasswordMessage(err.message || 'Failed to set password');
      } finally {
        confirmBtn.disabled = false;
        confirmBtn.textContent = 'Confirm';
      }
    });

    resetBtn.addEventListener('click', async () => {
      if (!window.confirm(`Send password reset email to ${pilot.name}?`)) return;
      resetBtn.disabled = true;
      resetBtn.textContent = 'Sending…';
      pwMessage.style.display = 'none';
      try {
        await resetUserPassword(pilot.id);
        showPasswordMessage(`Password reset email sent to ${pilot.name}`, 'success');
      } catch (err) {
        showPasswordMessage(err.message || 'Failed to send reset email');
      } finally {
        resetBtn.disabled = false;
        resetBtn.textContent = 'Send Reset Email';
      }
    });

    actionsSection.appendChild(setPasswordForm);
    infoSection.appendChild(actionsSection);

    // ── Alias relationship section (shown only for alias records) ────────
    if (pilot.role === 'alias') {
      const aliasSection = document.createElement('div');
      aliasSection.className = 'pilot-detail__alias-info';
      aliasSection.style.cssText = 'margin-top:0.75rem;padding:0.75rem;border:1px solid var(--color-border);border-radius:var(--radius);background:var(--color-bg);';

      const aliasHeading = document.createElement('h3');
      aliasHeading.textContent = 'Alias Relationship';
      aliasHeading.style.cssText = 'font-size:0.875rem;margin-bottom:0.5rem;';
      aliasSection.appendChild(aliasHeading);

      const parentInfo = document.createElement('p');
      parentInfo.style.cssText = 'font-size:0.85rem;margin-bottom:0.5rem;';
      parentInfo.textContent = `Parent User: ${pilot.parent?.name || 'Unknown'}`;
      aliasSection.appendChild(parentInfo);

      if (pilot.description) {
        const descInfo = document.createElement('p');
        descInfo.style.cssText = 'font-size:0.85rem;color:var(--color-text-muted);margin-bottom:0.5rem;';
        descInfo.textContent = `Description: ${pilot.description}`;
        aliasSection.appendChild(descInfo);
      }

      const removeAliasBtn = document.createElement('button');
      removeAliasBtn.type = 'button';
      removeAliasBtn.className = 'btn btn--danger';
      removeAliasBtn.textContent = 'Remove Alias';
      removeAliasBtn.addEventListener('click', async () => {
        if (!window.confirm(`Remove alias designation from ${pilot.name}? This will restore them as a regular pilot.`)) return;
        try {
          await updatePilot(pilot.id, {
            role: 'pilot',
            parent_id: null,
            active: true,
          });
          await refreshPilots();
          showMessage(`${pilot.name} alias designation removed`);
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
        } catch (err) {
          showMessage(err.message || 'Failed to remove alias', 'error');
        }
      });
      aliasSection.appendChild(removeAliasBtn);

      infoSection.appendChild(aliasSection);
    }

    openDetailModal({
      title: `Edit Pilot — ${pilot.name}`,
      fields: [
        { name: 'name', label: 'Name', type: 'text', required: true },
        { name: 'email', label: 'Email', type: 'text', required: true },
        {
          name: 'role',
          label: 'Role',
          type: 'select',
          required: true,
          options: [
            { value: USER_ROLES.PILOT, label: 'Pilot' },
            { value: USER_ROLES.ADMIN, label: 'Admin' },
            { value: USER_ROLES.OWNER, label: 'Owner' },
          ],
        },
        { name: 'guest', label: 'Guest', type: 'checkbox' },
      ],
      values: {
        name: pilot.name,
        email: pilot.email,
        role: pilot.role,
        guest: pilot.guest || false,
      },
      customSections: [infoSection],
      onDelete: isOwnAccount
        ? undefined
        : async () => {
            if (!window.confirm(`Are you sure you want to delete ${pilot.name}? This cannot be undone.`)) {
              throw new Error('Cancelled');
            }
            await deletePilot(pilot.id);
            await refreshPilots();
            showMessage(`${pilot.name} has been deleted`);
          },
      onSave: async (formData) => {
        // Validate email
        const emailResult = validateEmail(formData.email);
        if (!emailResult.valid) {
          const err = new Error(emailResult.errors[0]);
          err.fieldErrors = { email: emailResult.errors[0] };
          throw err;
        }

        // Sole admin protection: prevent the only admin from changing own role to pilot
        if (
          isOwnAccount &&
          (pilot.role === 'admin' || pilot.role === 'owner') &&
          formData.role === 'pilot'
        ) {
          const adminCount = await getAdminCount();
          if (adminCount <= 1) {
            const err = new Error('Cannot change role: you are the sole admin');
            err.fieldErrors = { role: 'Cannot change role: you are the sole admin' };
            throw err;
          }
        }

        await updatePilot(pilot.id, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          guest: formData.guest,
        });
        await refreshPilots();
        showMessage('Pilot updated successfully');
      },
    });
  }

  /**
   * Handle bulk delete of selected pilots from multi-select mode.
   */
  async function handleBulkDelete() {
    if (selectedPilotIds.size === 0) return;
    const currentUser = getUser();
    const selectedPilots = pilots.filter((p) => selectedPilotIds.has(p.id));
    const ownAccountSelected = currentUser && selectedPilots.some((p) => p.id === currentUser.id);

    if (ownAccountSelected) {
      showMessage('You cannot delete your own account. Deselect yourself and try again.', 'error');
      return;
    }

    const names = selectedPilots.map((p) => p.name).join(', ');
    if (!window.confirm(`Are you sure you want to delete ${selectedPilots.length} pilot(s): ${names}? This cannot be undone.`)) return;

    let deleted = 0;
    let lastError = null;
    for (const pilot of selectedPilots) {
      try {
        await deletePilot(pilot.id);
        deleted++;
      } catch (err) {
        lastError = err;
      }
    }

    selectedPilotIds.clear();
    await refreshPilots();

    if (lastError) {
      showMessage(`Deleted ${deleted} of ${selectedPilots.length} pilots. Some deletions failed.`, 'error');
    } else {
      showMessage(`${deleted} pilot(s) deleted successfully`);
    }
  }

  /**
   * Fetch pilots from the API and re-render the grid.
   */
  async function refreshPilots() {
    try {
      pilots = await getPilots();
    } catch (err) {
      pilots = [];
      showMessage('Failed to load pilots', 'error');
    }
    renderGrid();
  }

  // Initial load
  refreshPilots();

  return section;
}

/**
 * Returns the CSS class for pilot rows based on role and active state.
 * Exported for testing (Property 14).
 * @param {Object} pilot
 * @returns {string|null}
 */
export function getPilotRowClass(pilot) {
  if (pilot.role === 'alias') return 'pilot-row--alias';
  return pilot.active === false ? 'pilot-row--inactive' : null;
}

/**
 * Checks whether the sole admin can change their role.
 * Exported for testing (Property 12).
 * @param {Object} pilot - The pilot attempting to change role
 * @param {string} newRole - The desired new role
 * @param {number} adminCount - Current count of active admins
 * @param {string} currentUserId - The authenticated user's ID
 * @returns {{ allowed: boolean, reason?: string }}
 */
export function canChangeRole(pilot, newRole, adminCount, currentUserId) {
  if (
    currentUserId === pilot.id &&
    (pilot.role === 'admin' || pilot.role === 'owner') &&
    newRole === 'pilot' &&
    adminCount <= 1
  ) {
    return { allowed: false, reason: 'Cannot change role: you are the sole admin' };
  }
  return { allowed: true };
}
